/**
 * User-facing texts defined once (docs/08 §3).
 *
 * - Control texts: exactly those of docs/05 §4–§7 and of the workbook formulas (`data/build_xlsx.py`,
 *   source of truth). Parametric numbers are written as plain integers, as Excel concatenation does
 *   (`"... residuo teorico "&X&" carte"` → "residuo teorico 5 carte", no thousands separator).
 *   Only exception (v1.0.2): where the workbook says "hub" alone the app says "hub San Romano".
 * - Approved public messages and Q&A IT/EN (docs/03 §06–§07) and the case summary (docs/03 §11):
 *   never edit them, they are approved by The Pokémon Company.
 */
import { formatNumber } from './format.js';
import type { ContentLanguage, TrafficLevel } from './types.js';

/** Prefix of every anomaly; `isAnomaly` = control text starting with it. */
export const ANOMALY_PREFIX = '⚠';

export const OK = 'OK';

export function isAnomalyText(text: string | null | undefined): boolean {
  return typeof text === 'string' && text.startsWith(ANOMALY_PREFIX);
}

// ---------------------------------------------------------------- movements (docs/05 §4)

export const MOVEMENT_CONTROLS = {
  missingDateTime: '⚠ Manca data/ora',
  missingPoint: '⚠ Manca il punto',
  missingQuantity: '⚠ Manca la quantità',
  hubNegative: '⚠ Giacenza hub San Romano negativa',
  pointNegative: '⚠ Giacenza punto negativa',
  defectiveNegative: '⚠ Giacenza carte difettose negativa',
  returnMismatch: (residual: number) => `⚠ Rientro non quadra: residuo teorico ${residual} carte`,
  notSigned: '⚠ Documento non firmato',
  ok: OK,
  /** Cancelled movements (docs/08 §5). */
  cancelled: (reason: string) => `Annullato: ${reason}`,
} as const;

// ---------------------------------------------------------------- reports (docs/05 §5)

export const REPORT_CONTROLS = {
  /** Workbook text for "dati mancanti". */
  missingData: '⚠ Mancano data, ora o punto',
  progressiveDecreased: '⚠ Progressivo inferiore al report precedente',
  distributedExceedsDelivered: '⚠ Distribuite più carte di quelle consegnate',
  countDifference: (difference: number) => `⚠ Differenza conteggio: ${difference}`,
  notSent: 'Da inviare a Event Leads',
  ok: OK,
  cancelled: (reason: string) => `Annullato: ${reason}`,
} as const;

// ---------------------------------------------------------------- traffic light (docs/05 §6)

export const TRAFFIC_CONTROLS = {
  missingDateTime: '⚠ Manca data/ora',
  resumeAwaitingConfirmations: 'Ripresa: attendere conferme dai punti',
  missingClosureConfirmations: '⚠ Mancano conferme di chiusura dai punti',
  cardsToSecure: '⚠ Carte da mettere in sicurezza',
  ok: OK,
} as const;

/** Dashboard "livello semaforo attuale" sub-caption (workbook Cruscotto!K8). */
export const TRAFFIC_STATUS_TEXTS = {
  noChanges: 'nessuna variazione registrata',
  /** `dayMonth` = "dd/mm", `time` = "HH:MM". */
  since: (dayMonth: string, time: string) => `dal ${dayMonth} ore ${time}`,
} as const;

/**
 * "Azioni richieste" of each traffic-light level (docs/03 §04, Livelli operativi), one item per
 * sentence of the staff sheet: the checklist of the points when the level changes.
 */
export const TRAFFIC_REQUIRED_ACTIONS: Record<TrafficLevel, readonly string[]> = {
  VERDE: [
    'Una carta per ogni titolo validato dal palmare carte',
    'Monitoraggio costante della folla',
    'Aggiornamenti periodici al responsabile FMF',
  ],
  ARANCIONE: [
    'Stop scansione e consegna',
    'Carte subito fuori dalla vista del pubblico',
    'Conferma la chiusura al responsabile FMF',
    'Solo i testi della sezione 06',
  ],
  ROSSO: [
    'Stop scansione e consegna',
    'Carte fuori vista, imballate e custodite',
    'Conferma la chiusura al responsabile FMF',
    'Segui la Security',
    'Solo i testi della sezione 07',
  ],
};

/** Message shown when a report with growing public deliveries is blocked in ARANCIONE/ROSSO (docs/08 §7). */
export const TRAFFIC_BLOCK_MESSAGE = (level: Exclude<TrafficLevel, 'VERDE'>) =>
  `Livello ${level}: distribuzione sospesa. Non è possibile registrare nuove consegne al pubblico.`;

// ---------------------------------------------------------------- closure (docs/05 §7)

export const CLOSURE_HUB_OUTCOMES = {
  countMissing: 'Conteggio da inserire',
  ok: 'OK – quadrato',
  difference: (difference: number) =>
    `⚠ Differenza di ${difference} carte: verificare e rettificare`,
} as const;

export const CLOSURE_POINT_OUTCOMES = {
  none: '—',
  emptiedAndBalanced: 'OK – punto svuotato e quadrato',
  allDistributed: 'Carte tutte distribuite',
  inProgress: (residual: number) => `In corso: ${residual} carte al punto`,
  residualDifference: (residual: number) => `⚠ Residuo/differenza di ${residual} carte`,
} as const;

// ---------------------------------------------------------------- dashboard (docs/05 §7)

export const DASHBOARD_POINT_ALERTS = {
  /** Workbook text (Cruscotto!K11); docs/05 §7 abbreviates it as "⚠ Giacenza negativa". */
  negativeStock: '⚠ Giacenza negativa: verificare',
  residualAfterReturn: (stock: number) => `⚠ Residuo dopo il rientro: ${stock} carte`,
  reportLate: '⚠ Report in ritardo',
  supplied: 'OK – punto rifornito',
  noCards: 'Nessuna carta al punto',
} as const;

/**
 * Entries of the dashboard alert list (app only, not in the workbook): the control text of the
 * record prefixed with what it refers to, e.g. "CON-005: ⚠ Documento non firmato".
 */
export const DASHBOARD_ALERT_TEXTS = {
  point: (pointName: string, alert: string) => `${pointName}: ${alert}`,
  movement: (docNumber: string, control: string) => `${docNumber}: ${control}`,
  /** `when` = "dd/mm HH:MM". */
  report: (pointName: string, when: string, control: string) =>
    `${['Report', pointName, when].filter(Boolean).join(' ')}: ${control}`,
  traffic: (level: TrafficLevel, when: string, control: string) =>
    `${['Semaforo', level, when].filter(Boolean).join(' ')}: ${control}`,
  /** `dayMonth` = "dd/mm". */
  closureHub: (dayMonth: string, outcome: string) =>
    `Chiusura hub San Romano ${dayMonth}: ${outcome}`,
  closurePoint: (pointName: string, dayMonth: string, outcome: string) =>
    `Chiusura ${pointName} ${dayMonth}: ${outcome}`,
} as const;

/** Labels of the "Controlli generali" block (workbook Cruscotto). */
export const DASHBOARD_CHECK_LABELS = {
  movementAnomalies: 'Movimenti con anomalie (⚠)',
  unsignedDocuments: 'Documenti non firmati in duplice copia',
  reportAnomalies: 'Report dei punti con anomalie (⚠)',
  reportsNotSent: 'Report non ancora inviati agli Event Leads',
  trafficMissingConfirmations: 'Cambi semaforo con conferme mancanti (⚠)',
  defectiveReplaced: 'Carte difettose sostituite e rientrate (totale)',
  defectiveInCustody: "Carte difettose in custodia all'hub San Romano (da restituire a TPC)",
  totalFmf: "Carte in carico a FMF (carte buone all'hub San Romano + difettose + punti)",
} as const;

// ---------------------------------------------------------------- Event Leads summary (PROMPT §4.4)

/** Lines of the ready-to-copy update for the TPC Event Leads, in one language. */
export interface EventLeadsSummaryTexts {
  title: (eventName: string) => string;
  /** `date` = "dd/mm/yyyy", `time` = "HH:MM" (Italian formats in both languages). */
  updatedAt: (date: string, time: string) => string;
  /** `since` = caption of the level (`since` / `noChanges`). */
  level: (level: TrafficLevel, since: string) => string;
  /** `dayMonth` = "dd/mm", `time` = "HH:MM". */
  since: (dayMonth: string, time: string) => string;
  noChanges: string;
  distributedToday: (n: string) => string;
  stock: (n: string) => string;
  /** `when` = "HH:MM" for today, "dd/mm HH:MM" otherwise. */
  lastReport: (when: string, level: TrafficLevel) => string;
  noReport: string;
  separator: string;
  totalDistributedToday: (n: string) => string;
  totalStock: (n: string) => string;
  /**
   * San Romano hub stock at the instant (only with the stocks, during the day): `n` = "12.638", or
   * `hubCards` when damaged cards are in custody.
   */
  hubStock: (n: string) => string;
  /**
   * "4.427 carte buone + 3 difettose": good cards, then the damaged ones only when > 0 (singular
   * for 1). Numbers are formatted here (1.234) because the wording depends on them.
   */
  hubCards: (good: number, damaged: number) => string;
  /** End of day, hub count entered in «Chiusura giornaliera»: `cards` = `hubCards`. */
  hubStockAtClosingCounted: (cards: string) => string;
  /**
   * End of day without the count but after the evening pickup: theoretical stock, `cards` =
   * `hubCards`.
   */
  hubStockAtClosingExpected: (cards: string) => string;
  /**
   * Closing check of the count: `difference` = counted − theoretical (< 0 missing, > 0 more,
   * 0 matches), `expected` = theoretical stock ("4.449").
   */
  closingCheck: (difference: number, expected: string) => string;
  /**
   * Evening pickup from TPC for the operating day `date` ("dd/mm/yyyy"): `quantity` cards,
   * `documents` = `pickupDocument` items joined by `documentSeparator`.
   */
  eveningPickup: (date: string, quantity: number, documents: string) => string;
  /** `when` = "HH:MM" on the Rome date of the summary, "dd/mm HH:MM" otherwise. */
  pickupDocument: (docNumber: string, when: string) => string;
  documentSeparator: string;
  /** Stock at closing plus the evening pickups: `cards` = `hubCards`. */
  hubStockOvernight: (cards: string) => string;
}

/**
 * Names of the levels in the English summary, as in the TPC SOP (docs/02: GREEN, "Promo Pause",
 * "Shut Down"); the Italian text keeps the level of the engine (VERDE / ARANCIONE / ROSSO).
 */
export const TRAFFIC_LEVEL_NAMES_EN: Record<TrafficLevel, string> = {
  VERDE: 'GREEN',
  ARANCIONE: 'ORANGE',
  ROSSO: 'RED',
};
const TRAFFIC_LEVEL_SOP_EN: Record<TrafficLevel, string> = {
  VERDE: 'GREEN',
  ARANCIONE: 'ORANGE – Promo Pause',
  ROSSO: 'RED – Shut Down',
};

/** "1 carta buona", "2 carte buone"… (`one` for 1, `many` otherwise; numbers as 1.234). */
function countOf(n: number, one: string, many: string): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

/** `eventLeadsSummary` texts per language: Italian for the staff, English for the Event Leads. */
export const EVENT_LEADS_SUMMARY_TEXTS: Record<ContentLanguage, EventLeadsSummaryTexts> = {
  it: {
    title: (eventName) => `Distribuzione carte promozionali Pokémon – ${eventName}`,
    updatedAt: (date, time) => `Aggiornamento del ${date} ore ${time}`,
    level: (level, since) => `Livello semaforo: ${level} (${since})`,
    since: TRAFFIC_STATUS_TEXTS.since,
    noChanges: TRAFFIC_STATUS_TEXTS.noChanges,
    distributedToday: (n) => `distribuite oggi ${n}`,
    stock: (n) => `giacenza ${n}`,
    lastReport: (when, level) => `ultimo report ${when} (${level})`,
    noReport: 'nessun report',
    separator: ' · ',
    totalDistributedToday: (n) => `Totale distribuite oggi: ${n}`,
    totalStock: (n) => `Totale giacenza ai punti: ${n}`,
    hubStock: (n) => `Giacenza hub San Romano: ${n}`,
    hubCards: (good, damaged) =>
      damaged > 0
        ? `${countOf(good, 'carta buona', 'carte buone')} + ${countOf(damaged, 'difettosa', 'difettose')}`
        : countOf(good, 'carta buona', 'carte buone'),
    hubStockAtClosingCounted: (cards) =>
      `Giacenza hub San Romano alla chiusura (contata): ${cards}`,
    hubStockAtClosingExpected: (cards) =>
      `Giacenza hub San Romano alla chiusura (teorica, conteggio da inserire): ${cards}`,
    closingCheck: (difference, expected) => {
      const n = Math.abs(difference);
      if (difference < 0) {
        const missing = n === 1 ? 'manca 1 carta' : `mancano ${formatNumber(n)} carte`;
        return `Verifica di chiusura: ${missing} della distribuzione di oggi (teorica ${expected})`;
      }
      if (difference > 0) {
        return `Verifica di chiusura: ${countOf(n, 'carta', 'carte')} in più del previsto (teorica ${expected})`;
      }
      return `Verifica di chiusura: il conteggio quadra con la teorica ${expected}`;
    },
    eveningPickup: (date, quantity, documents) =>
      `Ritiro serale da TPC per il ${date}: ${countOf(quantity, 'carta', 'carte')} (${documents})`,
    pickupDocument: (docNumber, when) => `${docNumber} ore ${when}`,
    documentSeparator: ', ',
    hubStockOvernight: (cards) => `Giacenza hub San Romano per la notte: ${cards}`,
  },
  en: {
    title: (eventName) => `Pokémon promotional card distribution – ${eventName}`,
    updatedAt: (date, time) => `Update as of ${date} at ${time}`,
    level: (level, since) => `Traffic light: ${TRAFFIC_LEVEL_SOP_EN[level]} (${since})`,
    since: (dayMonth, time) => `since ${dayMonth} at ${time}`,
    noChanges: 'no change recorded',
    distributedToday: (n) => `distributed today ${n}`,
    stock: (n) => `stock ${n}`,
    lastReport: (when, level) => `last report ${when} (${TRAFFIC_LEVEL_NAMES_EN[level]})`,
    noReport: 'no report yet',
    separator: ' · ',
    totalDistributedToday: (n) => `Total distributed today: ${n}`,
    totalStock: (n) => `Total stock at the points: ${n}`,
    hubStock: (n) => `San Romano hub stock: ${n}`,
    hubCards: (good, damaged) =>
      damaged > 0
        ? `${countOf(good, 'good card', 'good cards')} + ${countOf(damaged, 'damaged card', 'damaged cards')}`
        : countOf(good, 'good card', 'good cards'),
    hubStockAtClosingCounted: (cards) => `San Romano hub stock at closing (counted): ${cards}`,
    hubStockAtClosingExpected: (cards) =>
      `San Romano hub stock at closing (expected, count not entered yet): ${cards}`,
    closingCheck: (difference, expected) => {
      const n = Math.abs(difference);
      if (difference < 0) {
        return `Closing check: ${countOf(n, 'card', 'cards')} missing from today's distribution (expected ${expected})`;
      }
      if (difference > 0) {
        return `Closing check: ${countOf(n, 'card', 'cards')} more than expected (expected ${expected})`;
      }
      return `Closing check: the count matches the expected ${expected}`;
    },
    eveningPickup: (date, quantity, documents) =>
      `Evening pickup from TPC for ${date}: ${countOf(quantity, 'card', 'cards')} (${documents})`,
    pickupDocument: (docNumber, when) => `${docNumber} at ${when}`,
    documentSeparator: ', ',
    hubStockOvernight: (cards) => `San Romano hub stock overnight: ${cards}`,
  },
};

// ---------------------------------------------------------------- staff sheet (docs/03)

/** A text in Italian and English (staff sheet, and the labels of the print forms in `apps/api`). */
export interface BilingualText {
  it: string;
  en: string;
}

export interface ApprovedQa {
  question: BilingualText;
  answer: BilingualText;
}

export interface ApprovedLevelTexts {
  level: Exclude<TrafficLevel, 'VERDE'>;
  /** Section title of docs/03. */
  title: string;
  intro: string;
  publicMessage: BilingualText;
  qa: ApprovedQa[];
  notes: { title: string; text: string }[];
  /** Ordered steps (ROSSO: point closure). */
  steps: string[];
}

/** docs/03 §06 – Livello Arancione. */
export const APPROVED_TEXTS_ARANCIONE: ApprovedLevelTexts = {
  level: 'ARANCIONE',
  title: 'Livello Arancione · cosa dire al pubblico',
  intro:
    'Usa esclusivamente i testi approvati da The Pokémon Company, senza modificarli. Per domande non previste, ripeti il messaggio al pubblico.',
  publicMessage: {
    it: "La distribuzione delle carte promozionali Pokémon è momentaneamente sospesa. In nessun punto di distribuzione di Lucca vengono consegnate carte. Vi invitiamo a seguire i canali ufficiali dell'evento per gli aggiornamenti. Grazie per la pazienza e la collaborazione.",
    en: 'The Pokémon promotional card distribution is currently paused. No cards are being issued at any of the distribution locations across Lucca. Please monitor official event communications for further updates. Thank you for your patience and cooperation.',
  },
  qa: [
    {
      question: { it: 'Sono finite le carte?', en: 'Are all the cards gone?' },
      answer: {
        it: "La distribuzione è momentaneamente sospesa. Vi invitiamo a seguire i canali ufficiali dell'evento per gli aggiornamenti.",
        en: 'Distribution is currently paused. Please monitor official event communications for further updates.',
      },
    },
    {
      question: { it: 'Quando riparte?', en: 'When will distribution restart?' },
      answer: {
        it: "Al momento non abbiamo aggiornamenti da comunicare. Vi invitiamo a seguire i canali ufficiali dell'evento.",
        en: 'We do not have an update to share at this time. Please monitor official event communications for further updates.',
      },
    },
    {
      question: { it: 'Posso restare in fila?', en: 'Can I remain in line?' },
      answer: {
        it: "Vi preghiamo di seguire le indicazioni dello staff dell'evento sulla gestione della coda.",
        en: 'Please follow instructions from the event staff regarding queue management.',
      },
    },
  ],
  notes: [
    {
      title: "Sospensione oltre un'ora",
      text: "La coda viene progressivamente sciolta. Invita i visitatori a proseguire con le altre attività dell'evento ed evita di creare aspettative su una ripresa a breve.",
    },
    {
      title: 'Ripresa della distribuzione',
      text: 'Solo quando la Security conferma condizioni stabili e vie di emergenza libere e gli Event Leads dichiarano il Verde. Il ritorno al Verde te lo comunica un responsabile FMF.',
    },
  ],
  steps: [],
};

/** docs/03 §07 – Livello Rosso. */
export const APPROVED_TEXTS_ROSSO: ApprovedLevelTexts = {
  level: 'ROSSO',
  title: 'Livello Rosso · cosa dire al pubblico',
  intro:
    'Usa esclusivamente i testi approvati da The Pokémon Company, senza modificarli. Non comunicare a nessuno i motivi della chiusura né le quantità rimaste.',
  publicMessage: {
    it: "La distribuzione delle carte promozionali Pokémon a Lucca è terminata. Ringraziamo i visitatori per l'entusiasmo e la collaborazione e chiediamo di seguire le indicazioni dello staff e del personale di sicurezza.",
    en: 'The Pokémon promotional card distribution at Lucca has now concluded. We thank attendees for their enthusiasm and cooperation and ask that guests follow directions from event staff and security personnel.',
  },
  qa: [
    {
      question: {
        it: 'Ci saranno ancora carte?',
        en: 'Will any cards be available at any point?',
      },
      answer: {
        it: 'A Lucca non verranno distribuite altre carte promozionali Pokémon in nessun punto di distribuzione.',
        en: 'No further Pokémon promotional cards will be issued at Lucca from any of the distribution locations.',
      },
    },
    {
      question: {
        it: 'Come posso ottenere la carta?',
        en: 'How do I claim a promo card if distribution is no longer happening?',
      },
      answer: {
        it: "Vi invitiamo a seguire i canali ufficiali dell'evento per eventuali aggiornamenti sulla distribuzione delle carte Pokémon.",
        en: 'Please monitor official event communications for any further updates relating to the Pokémon promo card distribution.',
      },
    },
    {
      question: {
        it: 'Perché è stata interrotta?',
        en: 'Why was the promo card distribution stopped?',
      },
      answer: {
        it: 'È stato deciso di concludere la distribuzione delle carte promozionali Pokémon. Non abbiamo altro da comunicare. Grazie per la collaborazione.',
        en: 'The decision was made to conclude the Pokémon promotion card distribution. We have nothing further to share at this time. Thank you for your cooperation.',
      },
    },
  ],
  notes: [],
  steps: [
    'Interrompi scansione e consegna; carte subito fuori dalla vista del pubblico.',
    "Segui la Security per allontanare il pubblico dall'area di distribuzione.",
    "Collabora alla chiusura delle corsie e all'aggiornamento della segnaletica.",
    'Imballa e custodisci le carte; attendi indicazioni FMF per conteggio e trasferimento.',
  ],
};

export const APPROVED_TEXTS: Record<Exclude<TrafficLevel, 'VERDE'>, ApprovedLevelTexts> = {
  ARANCIONE: APPROVED_TEXTS_ARANCIONE,
  ROSSO: APPROVED_TEXTS_ROSSO,
};

/** Fixed staff phrases (docs/03 Regola n. 1, §03, §05). */
export const STAFF_PHRASES = {
  rule1:
    'Non prendere nessuna iniziativa. Rispetta le indicazioni ricevute. Ascolta solo i responsabili autorizzati (sezione 3).',
  thirdPartyRequests:
    'Faccio riferimento ai responsabili FMF della distribuzione: la richiesta deve essere inoltrata a loro',
  press:
    'Non sono autorizzato a rilasciare dichiarazioni, la metto in contatto con il mio responsabile',
  emergencyException:
    "Esegui senza indugio le disposizioni di Forze dell'Ordine, Vigili del Fuoco, soccorso sanitario e Security dell'evento, mettiti in sicurezza e informa un responsabile FMF appena possibile.",
} as const;

export type CaseOutcome = '✔' | '✖' | '☎' | 'ℹ';

export const CASE_OUTCOME_LABELS: Record<CaseOutcome, string> = {
  '✔': 'Consegna',
  '✖': 'Non consegnare',
  '☎': 'Chiama un responsabile FMF',
  ℹ: 'Segui la procedura',
};

export interface CaseSummary {
  outcome: CaseOutcome;
  situation: string;
  action: string;
}

/** docs/03 §11 – Riepilogo dei casi. */
export const CASES: readonly CaseSummary[] = [
  { outcome: '✔', situation: 'Luce verde del palmare carte', action: 'Consegna 1 carta' },
  {
    outcome: '✖',
    situation: 'Luce rossa del palmare carte',
    action: 'Nessuna carta, senza eccezioni',
  },
  {
    outcome: '✖',
    situation: 'Solo braccialetto, senza biglietto TicketOne',
    action: 'Nessuna carta: serve il biglietto TicketOne',
  },
  {
    outcome: '✖',
    situation: "Nessun titolo leggibile, anche Forze dell'Ordine, staff o Security",
    action: 'Nessuna carta',
  },
  {
    outcome: '✔',
    situation: 'Una persona presenta più biglietti o più pass',
    action: 'Una sola carta, per il titolo della persona presente',
  },
  {
    outcome: '☎',
    situation: 'Sospetto che la stessa persona rifaccia la coda',
    action: 'Nega la consegna e chiama un responsabile FMF',
  },
  {
    outcome: '✖',
    situation: 'Pass espositore o ospite che chiede di saltare la coda',
    action: 'Non consentito: fa la coda con il pubblico',
  },
  {
    outcome: 'ℹ',
    situation: 'Pacchetto Level Up',
    action: "Indirizza la persona ai referenti dell'area Level UP",
  },
  {
    outcome: '✔',
    situation: 'Genitore con titolo valido e bambino nato dopo il 01/01/2017',
    action: 'Braccialetto bianco, doppia scansione, carta a entrambi insieme',
  },
  {
    outcome: '✖',
    situation: 'Titolo del genitore già utilizzato, con bambino',
    action: 'Niente braccialetto e niente carta al bambino',
  },
  {
    outcome: '✖',
    situation: 'Carta richiesta per un bambino non presente',
    action: 'Nessuna carta, nemmeno al genitore: tornare insieme',
  },
  {
    outcome: '✖',
    situation: 'Minore non accompagnato',
    action: 'Niente braccialetto e niente carta',
  },
  {
    outcome: '☎',
    situation: 'Un solo adulto con più di quattro bambini',
    action: 'Sospendi e chiama un responsabile FMF',
  },
  {
    outcome: 'ℹ',
    situation: 'Contestazione sulla qualità della carta',
    action: "Confronta con l'immagine di riferimento; sostituisci solo se fuori parametri",
  },
  {
    outcome: '☎',
    situation: 'Palmare guasto o non funzionante',
    action: 'Sospendi la consegna e chiama un responsabile FMF',
  },
  {
    outcome: '☎',
    situation: 'Richieste di Lucca Crea, TPC, TicketOne o altri',
    action: 'Rimanda ai responsabili FMF (sezione 03)',
  },
  {
    outcome: '✖',
    situation: 'Livello Arancione o Rosso comunicato da FMF',
    action: 'Stop scansione e consegna, carte fuori vista, solo testi approvati',
  },
  {
    outcome: '☎',
    situation: 'Qualsiasi dubbio',
    action: 'Non consegnare e chiama un responsabile FMF',
  },
];
